import { Skeleton, toast } from "@credit-acceptance/uikit";
import { CancelledError, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useEffect, useId, useRef, useState } from "react";
import { type Control, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { Input } from "../../../../../components/ui/uikit/input";
import { Textarea } from "../../../../../components/ui/uikit/textarea";
import { computeShouldForceExit } from "../../../../../hooks/useForceExitCheck";
import { formatSsn } from "../../../../../lib/format";
import { getErrorMessage, isHttpError } from "../../../../../lib/query/errors";
import { cn } from "../../../../../lib/utils";
import { useForceExitStore } from "../../../../../stores/forceExitStore";
import ForceExitWarningModal from "../../../../exit/ForceExitWarningModal";
import { applicationStatusQuery } from "../../../api/useApplicationStatus";
import { useDscWorkbench } from "../../../api/useDscWorkbench";
import { filterHistoryEntriesForEmployment, formatHistoryAnswerValue } from "../api/callScript.mapping";
import type { CallScriptSaveWarning } from "../api/callScript.savePayload";
import { type CallScriptVerifiedFields, EMPTY_CALL_SCRIPT_VERIFIED_FIELDS } from "../api/callScript.types";
import {
  buildSavedFieldsForAnswers,
  mapEmploymentToSavedFields,
  NO_SAVED_COUNTERPART_FIELDS,
} from "../api/callScriptEmploymentMapping";
import { buildQuestionPkMap } from "../api/callScriptQuestions";
import { type CallScriptFieldStates, validateEmploymentVerification } from "../api/callScriptValidation";
import { useCallScriptHistory } from "../api/useCallScriptHistory";
import { ensureCallScriptQuestionsForPerson, prefetchAllCallScriptQuestions } from "../api/useCallScriptQuestions";
import { useSaveCallScript } from "../api/useSaveCallScript";
import { isCompleteZip, useZipLookup } from "../api/useZipLookup";
import {
  EXTENSION_MASK_PLACEHOLDER,
  EXTENSION_TEXT_PLACEHOLDER,
  INCOME_TYPE_OPTIONS,
  OCCUPATION_INCOME_TYPE_CODES,
  PAY_FREQUENCY_OPTIONS,
  stripMaskPlaceholder,
  US_STATE_OPTIONS,
} from "../constants";
import type { ZipLookupStatus } from "../fields/useZipAutofill";
import { ZipLookupMessage } from "../fields/ZipLookupMessage";
import { useFormReadOnly } from "../readOnlyContext";
import type { SignersInput } from "../Signers.schema";
import {
  buildChangedFieldRows,
  type ChangedFieldRow,
  type ComparisonRowOfKinds,
  isRowChanged,
} from "./callScriptComparison";
import { CallScriptConfirmSaveDialog } from "./CallScriptConfirmSaveDialog";
import { CallScriptDialogShell } from "./CallScriptDialogShell";
import { CallScriptFieldControl } from "./CallScriptFieldControl";
import { CallScriptHistoryPanel } from "./CallScriptHistoryPanel";
import { CallScriptSaveBanners } from "./CallScriptSaveBanners";
import { useCallScriptOpenOrchestration } from "./useCallScriptOpenOrchestration";

type PersonPath = "driver" | "secondSigner";

interface CallScriptModalProperties {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Deal / approval identifier — path param for both Call Script calls, and displayed in the side panel. */
  readonly approvalId?: string;
  /** Applicant identifier — path param for both Call Script calls. */
  readonly applicantId?: string;
  /** Which person's income this call is for — selects the saved Employment record from the dashboard. */
  readonly personPath: PersonPath;
  /** Index of the income block within that person's incomes. */
  readonly index: number;
  /** RHF control — reads the Driver's name/SSN for the side panel. */
  readonly control: Control<SignersInput>;
}

/** The control kinds this script uses — residence's `yesNoBlank` has no employment row. */
type EmploymentRowKind = "text" | "phone" | "number" | "currency" | "date" | "checkbox" | "yesNo" | "select";

type VerifiedRowDefinition = ComparisonRowOfKinds<keyof CallScriptVerifiedFields, EmploymentRowKind> & {
  /**
   * When set, the row (both columns) only renders while this predicate is
   * true (e.g. Physical Work Location / Occupation fields). Takes `saved`
   * too — Physical Work Location rows need it so a saved work location isn't
   * hidden behind the (always-reset-to-blank) Verified checkbox.
   */
  showWhen?: (verified: CallScriptVerifiedFields, saved: CallScriptVerifiedFields) => boolean;
};

function buildRows(t: TFunction): VerifiedRowDefinition[] {
  return [
    { key: "employerName", label: t("review.income.callScriptModal.fields.employerName"), kind: "text" },
    {
      key: "phoneNumberDialed",
      label: t("review.income.callScriptModal.fields.phoneNumberDialed"),
      kind: "phone",
      placeholder: "000-000-0000",
    },
    {
      key: "verificationOutsourced",
      label: t("review.income.callScriptModal.fields.verificationOutsourced"),
      kind: "yesNo",
    },
    {
      key: "originalNumberBad",
      label: t("review.income.callScriptModal.fields.originalNumberBad"),
      kind: "yesNo",
    },
    { key: "verifiedWith", label: t("review.income.callScriptModal.fields.verifiedWith"), kind: "text" },
    { key: "hireDate", label: t("review.income.callScriptModal.fields.hireDate"), kind: "date" },
    {
      key: "employmentType",
      label: t("review.income.callScriptModal.fields.employmentType"),
      kind: "select",
      options: INCOME_TYPE_OPTIONS,
      placeholder: "Select",
    },
    {
      key: "occupation",
      label: t("review.income.callScriptModal.fields.occupation"),
      kind: "text",
      showWhen: (verified) => OCCUPATION_INCOME_TYPE_CODES.has(verified.employmentType),
    },
    { key: "streetNumber", label: t("review.income.callScriptModal.fields.streetNumber"), kind: "text" },
    { key: "streetName", label: t("review.income.callScriptModal.fields.streetName"), kind: "text" },
    { key: "apartmentNumber", label: t("review.income.callScriptModal.fields.apartmentNumber"), kind: "text" },
    {
      key: "zip",
      label: t("review.income.callScriptModal.fields.zip"),
      kind: "text",
      placeholder: "00000",
    },
    { key: "city", label: t("review.income.callScriptModal.fields.city"), kind: "text" },
    {
      key: "state",
      label: t("review.income.callScriptModal.fields.state"),
      kind: "select",
      options: US_STATE_OPTIONS,
      placeholder: "Select",
    },
    {
      key: "employerPhone",
      label: t("review.income.callScriptModal.fields.employerPhone"),
      kind: "phone",
      placeholder: "000-000-0000",
    },
    {
      key: "differentWorkLocation",
      label: t("review.income.callScriptModal.fields.differentWorkLocation"),
      kind: "checkbox",
    },
    {
      key: "workLocationName",
      label: t("review.income.callScriptModal.fields.workLocationName"),
      kind: "text",
      showWhen: (verified, saved) => verified.differentWorkLocation || saved.differentWorkLocation,
    },
    {
      key: "workLocationStreetNumber",
      label: t("review.income.callScriptModal.fields.workLocationStreetNumber"),
      kind: "text",
      showWhen: (verified, saved) => verified.differentWorkLocation || saved.differentWorkLocation,
    },
    {
      key: "workLocationStreetName",
      label: t("review.income.callScriptModal.fields.workLocationStreetName"),
      kind: "text",
      showWhen: (verified, saved) => verified.differentWorkLocation || saved.differentWorkLocation,
    },
    {
      key: "workLocationApartmentNumber",
      label: t("review.income.callScriptModal.fields.workLocationApartmentNumber"),
      kind: "text",
      showWhen: (verified, saved) => verified.differentWorkLocation || saved.differentWorkLocation,
    },
    {
      key: "workLocationZip",
      label: t("review.income.callScriptModal.fields.workLocationZip"),
      kind: "text",
      placeholder: "00000",
      showWhen: (verified, saved) => verified.differentWorkLocation || saved.differentWorkLocation,
    },
    {
      key: "workLocationCity",
      label: t("review.income.callScriptModal.fields.workLocationCity"),
      kind: "text",
      showWhen: (verified, saved) => verified.differentWorkLocation || saved.differentWorkLocation,
    },
    {
      key: "workLocationState",
      label: t("review.income.callScriptModal.fields.workLocationState"),
      kind: "select",
      options: US_STATE_OPTIONS,
      placeholder: "Select",
      showWhen: (verified, saved) => verified.differentWorkLocation || saved.differentWorkLocation,
    },
    {
      key: "workLocationPhone",
      label: t("review.income.callScriptModal.fields.workLocationPhone"),
      kind: "phone",
      placeholder: "000-000-0000",
      showWhen: (verified, saved) => verified.differentWorkLocation || saved.differentWorkLocation,
    },
    { key: "dateOfTermination", label: t("review.income.callScriptModal.fields.dateOfTermination"), kind: "date" },
    {
      key: "payFrequency",
      label: t("review.income.callScriptModal.fields.payFrequency"),
      kind: "select",
      options: PAY_FREQUENCY_OPTIONS,
      placeholder: "Select",
    },
    {
      key: "grossPayPerPeriod",
      label: t("review.income.callScriptModal.fields.grossPayPerPeriod"),
      kind: "currency",
    },
    { key: "avgHoursPerWeek", label: t("review.income.callScriptModal.fields.avgHoursPerWeek"), kind: "number" },
  ];
}

/** i18n keys valid as a save-warning group's display heading. */
type WarningLabelKey =
  | "review.income.callScriptModal.warnings.employmentWritebackFailed"
  | "review.income.callScriptModal.warnings.generic";

/** Save-warning `code` -> the i18n key for its display heading. Codes with no entry here fall back to `GENERIC_WARNING_LABEL_KEY`. */
const WARNING_CODE_LABEL_KEYS: Partial<Record<string, WarningLabelKey>> = {
  employment_writeback_failed: "review.income.callScriptModal.warnings.employmentWritebackFailed",
};

const GENERIC_WARNING_LABEL_KEY: WarningLabelKey = "review.income.callScriptModal.warnings.generic";

// ---------------------------------------------------------------------------
// CallScriptModal (OR-21767)
//
// Draggable, dismissable "Call Script" modal opened from an income block's
// Employment Verification Phone row. Renders a "Verified Information" /
// "Saved Information" comparison for every employment-verification field
// (Saved is read from the dashboard's Employment record for this person and
// income index), plus a side panel with Approval #, Driver name/SSN,
// verification notes, and prior call history (only fetched while open).
//
// The Dialog shell always renders (so Escape/outside-click/focus behavior is
// always present); the data-driven body only renders once both ids are known.
//
// Save vs. close, deliberately split (a call script session can involve
// several saves before the analyst is done with this income row): a
// successful save (see `handleConfirmSave`) refreshes THIS employment's
// history, the dashboard, and the stipulations list
// — "Saved Information" is built from history (the dialed-phone row) plus
// the dashboard's Employment record (everything else, since the save
// actually persists changes there; the Signers form's own resync also
// reacts to that dashboard refresh — see useSignersForm) — and clears
// "Verified Information" back to blank for the next call. Closing the modal
// has nothing special to do as a result.
// ---------------------------------------------------------------------------
export function CallScriptModal({
  open,
  onOpenChange,
  approvalId,
  applicantId,
  personPath,
  index,
  control,
}: CallScriptModalProperties) {
  const { t } = useTranslation();
  const readOnly = useFormReadOnly();
  const hasIds = Boolean(approvalId && applicantId);
  const uid = useId();
  const queryClient = useQueryClient();
  const setShouldForceExit = useForceExitStore((state) => state.setShouldForceExit);

  const dashboard = useDscWorkbench(approvalId ?? "");
  const employment = dashboard.data?.[personPath]?.income?.employments?.[index];
  // The primary key of the applicant's primary (current/physical) residence
  // address, for the save contract's applicantAddressPk. Falls back to
  // mailing's id whenever current itself is absent OR present but not yet
  // carrying an id — v2 exposes no `previous[]`, so mailing is the only other
  // address slot that could carry one.
  const residence = dashboard.data?.[personPath]?.residence;
  const primaryAddressId = residence?.current?.id ?? residence?.mailing?.id;

  const driverFirstName = useWatch({ control, name: "driver.customerInfo.firstName" });
  const driverLastName = useWatch({ control, name: "driver.customerInfo.lastName" });
  const driverSsn = useWatch({ control, name: "driver.customerInfo.ssnOrItin" });
  const driverName = [driverFirstName, driverLastName].filter(Boolean).join(" ");
  const personLabel = t(personPath === "driver" ? "signers.driver" : "signers.secondSigner");

  const history = useCallScriptHistory(approvalId ?? "", applicantId ?? "", { enabled: open && hasIds });
  useEffect(() => {
    if (open && approvalId && applicantId) {
      prefetchAllCallScriptQuestions(queryClient, approvalId, applicantId);
    }
  }, [open, approvalId, applicantId, queryClient]);
  const saveCallScript = useSaveCallScript();
  const [note, setNote] = useState("");
  // A successful save can still carry non-fatal warnings (e.g. recap-service
  // couldn't write an invalid phone number back to Employment) — grouped by
  // `code` for display (see WARNING_CODE_LABEL_KEYS), cleared on open and
  // replaced (not appended) by each new save.
  const [saveWarnings, setSaveWarnings] = useState<CallScriptSaveWarning[]>([]);
  const [verified, setVerified] = useState<CallScriptVerifiedFields>(EMPTY_CALL_SCRIPT_VERIFIED_FIELDS);
  const [fieldStates, setFieldStates] = useState<CallScriptFieldStates>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [changedFieldRows, setChangedFieldRows] = useState<Array<ChangedFieldRow<keyof CallScriptVerifiedFields>>>([]);
  const [confirmError, setConfirmError] = useState<string>();
  const [isCheckingForceExit, setIsCheckingForceExit] = useState(false);
  // Fax Verification (spec §6.3) — a sibling flow triggered from the same
  // screen: save() runs immediately (no "are you sure" confirm, unlike the
  // main Save button), then a separate popup opens to fax a verification
  // letter. No BFF endpoint exists for actually sending the fax yet, so the
  // send action is a visible, disabled stub pending backend support.
  const [faxOpen, setFaxOpen] = useState(false);
  const [fax, setFax] = useState({ faxTo: "", faxCompanyName: "", faxNumber: "", faxAddtlNotes: "" });
  // Picking a date fires the DatePicker's onChange and onBlur in the same batch, so a
  // `verified` read from render-time closure in the onBlur handler below would still see
  // the pre-pick value. This ref is updated synchronously inside updateVerified (and on
  // reset), so the blur handler always validates against the value just picked/typed.
  const verifiedReference = useRef(verified);
  // Tracks the last ZIP the auto-lookup effect has already filled City/State
  // for, so it never re-fires (or fights a manual edit) for the same ZIP;
  // reset whenever "Verified Information" itself resets (on open, and after a
  // successful save).
  // eslint-disable-next-line unicorn/no-useless-undefined -- useRef's no-init overload still requires the arg here
  const lastAutofilledZipReference = useRef<string | undefined>(undefined);
  // Same tracking, for the Physical Work Location ZIP's own auto-lookup.
  // eslint-disable-next-line unicorn/no-useless-undefined -- useRef's no-init overload still requires the arg here
  const lastAutofilledWorkLocationZipReference = useRef<string | undefined>(undefined);
  // Guards the sticky-flag seeding effect below so it only runs once per
  // modal-open cycle -- reset (to false) alongside the rest of "Verified
  // Information" whenever the modal opens, and flipped true as soon as it
  // seeds, so it never re-fires (and clobbers) a manual edit made afterwards.
  const stickyFieldsSeededReference = useRef(false);
  // Tracks mount state for the deferred setTimeout in validateDateFieldOnBlur, so it
  // doesn't call setFieldStates after the modal (and its state) has been torn down.
  const isMountedReference = useRef(true);
  useEffect(
    () => () => {
      isMountedReference.current = false;
    },
    []
  );

  /**
   * Clears "Verified Information" back to blank — what the analyst captures
   * live on the call, never a copy of saved/previously-dialed data. Shared by
   * the on-open reset below and the post-save reset in `handleConfirmSave`.
   */
  function resetVerifiedInformation() {
    setVerified(EMPTY_CALL_SCRIPT_VERIFIED_FIELDS);
    verifiedReference.current = EMPTY_CALL_SCRIPT_VERIFIED_FIELDS;
    setFieldStates({});
    lastAutofilledZipReference.current = undefined;
    lastAutofilledWorkLocationZipReference.current = undefined;
    stickyFieldsSeededReference.current = false;
  }

  useEffect(() => {
    if (open) {
      resetVerifiedInformation();
      setNote("");
      setSaveWarnings([]);
      setConfirmOpen(false);
      setChangedFieldRows([]);
      setConfirmError(undefined);
      setFaxOpen(false);
      setFax({ faxTo: "", faxCompanyName: "", faxNumber: "", faxAddtlNotes: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resetVerifiedInformation is stable per render; only `open` should retrigger this
  }, [open]);

  // Modal-open orchestration (OR-21767 spec §2): before the analyst can start
  // a call, any unsaved edits elsewhere on the form must be persisted first —
  // legacy CAPS2 saved the whole Income tab (and re-ran stipulations/advance
  // recalc) before ever opening the popup. Shared with CallScriptModalResidence
  // — see useCallScriptOpenOrchestration.
  const { isPreparingModal, prepareError, forceExitWarningOpen, setForceExitWarningOpen, retry } =
    useCallScriptOpenOrchestration({
      open,
      approvalId,
      applicantId,
      readOnly,
      onOpenChange,
      prepareSaveFailedMessage: t("review.income.callScriptModal.prepareSaveFailed"),
    });

  const canSave =
    hasIds && !readOnly && !saveCallScript.isPending && !isCheckingForceExit && !isPreparingModal && !prepareError;
  const rows = buildRows(t);
  const latestHistoryEntryForEmployment = filterHistoryEntriesForEmployment(
    history.data?.entries ?? [],
    employment?.id
  )[0];
  const savedFields: CallScriptVerifiedFields = {
    ...mapEmploymentToSavedFields(employment),
    phoneNumberDialed: latestHistoryEntryForEmployment?.phoneNumberDialed ?? "",
  };
  const noSavedValue = t("review.income.callScriptModal.noSavedValue");

  // Verification Outsourced?/Was original number bad? are sticky flags, not
  // per-call answers (SVRS-56142): legacy CAPS2 carries them forward from the
  // last saved call until an analyst affirmatively changes them, so reopening
  // the modal must not silently reset a prior "Y" back to "N". Everything
  // else in "Verified Information" still starts blank (see
  // resetVerifiedInformation above) -- only these two fields are seeded, and
  // only once per open, once the history query has settled (so it always
  // seeds from the real prior value instead of racing the fetch and landing
  // on the blank default).
  useEffect(() => {
    if (!open || stickyFieldsSeededReference.current || history.isLoading) {
      return;
    }
    stickyFieldsSeededReference.current = true;
    const priorStickyFields = buildSavedFieldsForAnswers(employment, latestHistoryEntryForEmployment);
    setVerified((previous) => {
      const next = {
        ...previous,
        originalNumberBad: priorStickyFields.originalNumberBad,
        verificationOutsourced: priorStickyFields.verificationOutsourced,
      };
      verifiedReference.current = next;
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- employment/latestHistoryEntryForEmployment are recomputed every render; the ref guard (not the dep array) is what limits this to once per open cycle
  }, [open, history.isLoading]);

  // TanStack Query v5 dropped per-query `onError`, so query failures are
  // toasted from an effect (mirrors PullPurchasedEmployerInfo).
  // eslint-disable-next-line unicorn/no-useless-undefined -- useRef's no-init overload still requires the arg here
  const toastedHistoryErrorReference = useRef<unknown>(undefined);
  useEffect(() => {
    if (history.isError && history.error !== toastedHistoryErrorReference.current) {
      toastedHistoryErrorReference.current = history.error;
      toast.error(t("review.income.callScriptModal.history.error", { message: getErrorMessage(history.error) }));
    } else if (!history.isError) {
      toastedHistoryErrorReference.current = undefined;
    }
  }, [history.isError, history.error, t]);

  function updateVerified<K extends keyof CallScriptVerifiedFields>(key: K, value: CallScriptVerifiedFields[K]) {
    setVerified((previous) => {
      const next = { ...previous, [key]: value };
      verifiedReference.current = next;
      return next;
    });
    // Clear any submit-time error styling on this field as soon as the analyst enters or
    // selects a new value — it only reappears on the next Save click (or, for the date
    // fields, on the next blur).
    setFieldStates((previous) => {
      const stateKey = key as keyof CallScriptFieldStates;
      if (previous[stateKey] === undefined) {
        return previous;
      }
      const next = { ...previous };
      delete next[stateKey];
      return next;
    });
  }

  // Employee Physical Address ZIP auto-lookup (spec §4): once the analyst
  // enters a complete 5-digit ZIP, look up city/state and fill them in.
  // Mirrors the Residence tab's useZipAutofill, but this modal keeps its own
  // plain state (not RHF), so the query is wired directly here instead of
  // reusing that hook. Only fires once per distinct ZIP value so it never
  // fights a manual City/State edit made after the autofill.
  const zipForLookup = isCompleteZip(verified.zip) ? verified.zip : undefined;
  const zipLookup = useZipLookup(zipForLookup);
  useEffect(() => {
    if (!zipForLookup || lastAutofilledZipReference.current === zipForLookup) {
      return;
    }
    if (zipLookup.data) {
      lastAutofilledZipReference.current = zipForLookup;
      if (zipLookup.data.city) {
        updateVerified("city", zipLookup.data.city);
      }
      if (zipLookup.data.state) {
        updateVerified("state", zipLookup.data.state);
      }
      return;
    }
    // A settled not-found/error means City/State can't be trusted for this
    // ZIP — clear them instead of leaving the previous ZIP's values in place.
    if (zipLookup.isError) {
      lastAutofilledZipReference.current = zipForLookup;
      updateVerified("city", "");
      updateVerified("state", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateVerified is stable per render; only the lookup result should retrigger this
  }, [zipForLookup, zipLookup.data, zipLookup.isError]);
  const zipLookupStatus: ZipLookupStatus = (() => {
    if (!zipForLookup) {
      return "idle";
    }
    if (zipLookup.isFetching) {
      return "loading";
    }
    if (zipLookup.isError) {
      return isHttpError(zipLookup.error, 404) ? "not-found" : "error";
    }
    return "idle";
  })();

  // Physical Work Location ZIP auto-lookup — same behavior as the Employee
  // Physical Address ZIP above, but fills workLocationCity/workLocationState.
  const workLocationZipForLookup = isCompleteZip(verified.workLocationZip) ? verified.workLocationZip : undefined;
  const workLocationZipLookup = useZipLookup(workLocationZipForLookup);
  useEffect(() => {
    if (!workLocationZipForLookup || lastAutofilledWorkLocationZipReference.current === workLocationZipForLookup) {
      return;
    }
    if (workLocationZipLookup.data) {
      lastAutofilledWorkLocationZipReference.current = workLocationZipForLookup;
      if (workLocationZipLookup.data.city) {
        updateVerified("workLocationCity", workLocationZipLookup.data.city);
      }
      if (workLocationZipLookup.data.state) {
        updateVerified("workLocationState", workLocationZipLookup.data.state);
      }
      return;
    }
    // A settled not-found/error means City/State can't be trusted for this
    // ZIP — clear them instead of leaving the previous ZIP's values in place.
    if (workLocationZipLookup.isError) {
      lastAutofilledWorkLocationZipReference.current = workLocationZipForLookup;
      updateVerified("workLocationCity", "");
      updateVerified("workLocationState", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateVerified is stable per render; only the lookup result should retrigger this
  }, [workLocationZipForLookup, workLocationZipLookup.data, workLocationZipLookup.isError]);
  const workLocationZipLookupStatus: ZipLookupStatus = (() => {
    if (!workLocationZipForLookup) {
      return "idle";
    }
    if (workLocationZipLookup.isFetching) {
      return "loading";
    }
    if (workLocationZipLookup.isError) {
      return isHttpError(workLocationZipLookup.error, 404) ? "not-found" : "error";
    }
    return "idle";
  })();

  // On-blur validation for the date fields (hireDate, dateOfTermination), mirroring
  // the Signers form's RHF `onBlur` mode: only the field just left is (re)checked,
  // rather than waiting for the submit-time pass to flag it.
  //
  // Deferred via setTimeout: the calendar overlay's day buttons are real, focusable
  // <button>s, so clicking one blurs the text input mid-click (before the picker's own
  // click handling — value commit + refocusing the input — finishes). Validating
  // synchronously in that blur re-renders the modal while the picker is still mid-click,
  // which in real browsers (not reproducible in jsdom) races the picker's own click
  // handling and drops the selection. Pushing the validation to a fresh task lets the
  // picker finish its click handling first.
  function validateDateFieldOnBlur(key: keyof CallScriptVerifiedFields) {
    setTimeout(() => {
      if (!isMountedReference.current) {
        return;
      }
      const validatedKey = key as keyof CallScriptFieldStates;
      const result = validateEmploymentVerification(verifiedReference.current, savedFields);
      if (result.fieldStates[validatedKey] === undefined) {
        return;
      }
      setFieldStates((previous) => ({ ...previous, [validatedKey]: result.fieldStates[validatedKey] }));
    }, 0);
  }

  // Submit-time-only validation (OR-21767): every field is checked
  // independently and just drives per-field error styling — no popup on
  // failure. Once every field passes, the "CAPS WARNING" confirm dialog opens
  // with a Verified-vs-Saved comparison of only the changed fields — unless
  // nothing actually changed, in which case the save proceeds immediately.
  const handleSaveClick = () => {
    if (!canSave || !approvalId || !applicantId) {
      return;
    }
    const result = validateEmploymentVerification(verified, savedFields);
    setFieldStates(result.fieldStates);
    if (!result.isValid) {
      return;
    }
    const changed = buildChangedFieldRows(rows, verified, savedFields, {
      noSavedValue,
      alwaysChangedWhenEntered: (key) => NO_SAVED_COUNTERPART_FIELDS.has(key),
      hasSavedCounterpart: (key) => !NO_SAVED_COUNTERPART_FIELDS.has(key),
    });
    if (changed.length === 0) {
      void handleConfirmSave();
      return;
    }
    setChangedFieldRows(changed);
    setConfirmOpen(true);
  };

  // Re-checks force-exit fresh (bypassing the query's staleTime) at the
  // moment the confirm dialog's OK is clicked, mirroring ReviewPage's
  // handleSave — the button-click-to-open guard's cached store value could be
  // stale by the time the analyst confirms the save. `openFaxOnSuccess` is
  // set by the Fax Verification flow (spec §6.3), which saves immediately
  // with no confirm dialog and then opens the fax popup on success.
  const handleConfirmSave = async (options?: { openFaxOnSuccess?: boolean }) => {
    if (!approvalId || !applicantId) {
      return;
    }
    setConfirmError(undefined);
    // Cleared here (not just on modal open) so a warning from an earlier
    // successful save doesn't linger alongside a hard error from this attempt.
    setSaveWarnings([]);
    setIsCheckingForceExit(true);
    try {
      const status = await queryClient.fetchQuery({ ...applicationStatusQuery(approvalId), staleTime: 0 });
      const isForceExit = computeShouldForceExit({ isPreApprovalVoid: status.isPreApprovalVoid, readOnly });
      setShouldForceExit(isForceExit);

      if (isForceExit) {
        setConfirmOpen(false);
        setForceExitWarningOpen(true);
        return;
      }

      // Awaited (not read from cache synchronously) so a Save that races
      // ahead of the modal-open prefetch still resolves every field's
      // verificationQuestionPk instead of silently posting an empty
      // verificationAnswers[] — see ensureCallScriptQuestionsForPerson.
      const questions = await ensureCallScriptQuestionsForPerson(queryClient, approvalId, applicantId, personPath);

      setConfirmOpen(false);
      saveCallScript.mutate(
        {
          approvalId,
          applicantId,
          personPath,
          incomeIndex: index,
          applicantEmploymentPk: employment?.id,
          applicantAddressPk: primaryAddressId,
          note: note.trim(),
          verifiedFields: verified,
          savedFieldsForAnswers: buildSavedFieldsForAnswers(
            employment,
            filterHistoryEntriesForEmployment(history.data?.entries ?? [], employment?.id)[0]
          ),
          questionPkMap: buildQuestionPkMap(questions),
          employerPhoneExtension: stripMaskPlaceholder(
            employment?.phoneExtension ?? "",
            EXTENSION_MASK_PLACEHOLDER,
            EXTENSION_TEXT_PLACEHOLDER
          ),
        },
        {
          onSuccess: (result) => {
            // `result` is only `undefined` when useSaveCallScript's mutationFn
            // no-op'd (a force-exit was detected between this handler's own
            // check above and the mutation actually running) — nothing was
            // saved, so don't wipe what the analyst just captured on the call.
            if (!result) {
              return;
            }
            setNote("");
            resetVerifiedInformation();
            setSaveWarnings(result.warnings ?? []);
            if (options?.openFaxOnSuccess) {
              setFaxOpen(true);
            }
          },
        }
      );
    } catch (error) {
      if (!(error instanceof CancelledError)) {
        setConfirmError(getErrorMessage(error));
      }
    } finally {
      setIsCheckingForceExit(false);
    }
  };

  // Fax Verification's own entry point (spec §6.3): runs the same field
  // validation as the main Save button, but on success calls save()
  // immediately — no "changed fields" confirm dialog — then opens the fax
  // popup.
  const handleFaxVerificationClick = () => {
    if (!canSave || !approvalId || !applicantId) {
      return;
    }
    const result = validateEmploymentVerification(verified, savedFields);
    setFieldStates(result.fieldStates);
    if (!result.isValid) {
      return;
    }
    void handleConfirmSave({ openFaxOnSuccess: true });
  };

  const entries = filterHistoryEntriesForEmployment(history.data?.entries ?? [], employment?.id);
  const historyEntryHeader = t("review.income.callScriptModal.history.entryHeader", {
    incomeNumber: index + 1,
    personLabel: personLabel.toUpperCase(),
  });
  const errorMessages: Partial<Record<keyof CallScriptVerifiedFields, string>> = {
    employerName: t("review.income.callScriptModal.errors.employerName"),
    phoneNumberDialed: t("review.income.callScriptModal.errors.phoneNumberDialed"),
    hireDate: t("review.income.callScriptModal.errors.hireDate"),
    zip: t("review.income.callScriptModal.errors.zip"),
    employerPhone: t("review.income.callScriptModal.errors.employerPhone"),
    workLocationPhone: t("review.income.callScriptModal.errors.workLocationPhone"),
    dateOfTermination: t("review.income.callScriptModal.errors.dateOfTermination"),
  };

  return (
    <>
      <CallScriptDialogShell
        open={open}
        onOpenChange={onOpenChange}
        title={t("review.income.callScriptModal.title", { incomeNumber: index + 1, personLabel })}
        description={t("review.income.callScriptModal.description")}
        closeLabel={t("review.income.callScriptModal.close")}
        headerTestId="call-script-header"
      >
        {hasIds && isPreparingModal && (
          <div
            className="gap-(--spacing-3) px-(--spacing-6) py-(--spacing-8) bg-card flex flex-col"
            role="status"
            aria-label={t("review.income.callScriptModal.preparing")}
          >
            <Skeleton loading active paragraph={{ rows: 6 }} />
          </div>
        )}

        {hasIds && !isPreparingModal && prepareError && (
          <div className="gap-(--spacing-4) px-(--spacing-6) py-(--spacing-8) bg-card flex flex-col">
            <p
              role="alert"
              className="text-destructive text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height)"
            >
              {t("review.income.callScriptModal.prepareErrorPrefix", { message: prepareError })}
            </p>
            <div className="gap-(--spacing-2) flex">
              <Button type="button" size="xs" onClick={retry}>
                {t("review.income.callScriptModal.retry")}
              </Button>
              <Button type="button" variant="outline" size="xs" onClick={() => onOpenChange(false)}>
                {t("review.income.callScriptModal.exit")}
              </Button>
            </div>
          </div>
        )}

        {hasIds && !isPreparingModal && !prepareError && (
          <div className="flex flex-col">
            <div className="px-(--spacing-4) bg-card">
              <CallScriptSaveBanners
                error={
                  saveCallScript.isError
                    ? t("review.income.callScriptModal.saveError", { message: getErrorMessage(saveCallScript.error) })
                    : undefined
                }
                warnings={saveWarnings}
                warningHeading={(code) => t(WARNING_CODE_LABEL_KEYS[code] ?? GENERIC_WARNING_LABEL_KEY)}
              />
              <div className="gap-(--spacing-2) py-(--spacing-2) grid h-[75vh] grid-cols-1 lg:grid-cols-[2fr_1fr]">
                {/* Verified vs Saved comparison */}
                <div className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) grid min-h-0 grid-cols-1 overflow-y-auto sm:grid-cols-[auto_1fr_1fr]">
                  <div />
                  <div className="text-primary text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold)">
                    {t("review.income.callScriptModal.verifiedInformation")}
                  </div>
                  <div className="text-primary text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold)">
                    {t("review.income.callScriptModal.savedInformation")}
                  </div>
                  {rows
                    .filter((row) => !row.showWhen || row.showWhen(verified, savedFields))
                    .map((row) => {
                      const id = `${uid}-${row.key}`;
                      const hasSavedCounterpart = !NO_SAVED_COUNTERPART_FIELDS.has(row.key);
                      // Checkbox rows (currently just "Different Work Location") have no
                      // Saved-column comparison — the checkbox and its label live together
                      // in a single full-width row instead of the usual 3-column layout.
                      if (row.kind === "checkbox") {
                        return (
                          <div
                            key={row.key}
                            className="gap-(--spacing-2) py-(--spacing-0) flex items-center sm:col-span-2 sm:col-start-2"
                          >
                            <CallScriptFieldControl
                              row={row}
                              id={id}
                              value={verified[row.key]}
                              onChange={(value) => updateVerified(row.key, value)}
                              disabled={readOnly || saveCallScript.isPending}
                              ariaLabel={row.label}
                            />
                            <label
                              htmlFor={id}
                              className="text-primary text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-medium)"
                            >
                              {row.label}
                            </label>
                          </div>
                        );
                      }
                      return (
                        <div key={row.key} className="[&>*]:py-(--spacing-0) contents">
                          {/* Not a <label htmlFor>: clicking it must not focus/activate the Verified
                                input — CallScriptFieldControl already gets its accessible name via aria-label. */}
                          <span className="text-primary pr-(--spacing-2) text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-medium) self-center text-right">
                            {row.label}
                          </span>
                          <div
                            className={cn(
                              "max-w-[90%] self-center",
                              isRowChanged(row, verified[row.key], savedFields[row.key], {
                                alwaysChangedWhenEntered: (key) => NO_SAVED_COUNTERPART_FIELDS.has(key),
                              }) && "rounded-md bg-amber-100"
                            )}
                            data-testid={`${row.key}-verified-cell`}
                          >
                            <CallScriptFieldControl
                              row={row}
                              id={id}
                              value={verified[row.key]}
                              onChange={(value) => {
                                updateVerified(row.key, value);
                                // An incomplete ZIP never triggers the auto-lookup effect above,
                                // so clear City/State here as soon as the analyst edits away from
                                // a resolved ZIP — the not-found/error case is handled by that
                                // effect once the lookup settles.
                                if (row.key === "zip" && !isCompleteZip(value as string)) {
                                  updateVerified("city", "");
                                  updateVerified("state", "");
                                } else if (row.key === "workLocationZip" && !isCompleteZip(value as string)) {
                                  updateVerified("workLocationCity", "");
                                  updateVerified("workLocationState", "");
                                }
                              }}
                              onBlur={row.kind === "date" ? () => validateDateFieldOnBlur(row.key) : undefined}
                              disabled={readOnly || saveCallScript.isPending}
                              ariaLabel={row.label}
                              invalid={fieldStates[row.key as keyof CallScriptFieldStates] === "error"}
                            />
                            {fieldStates[row.key as keyof CallScriptFieldStates] === "error" && (
                              <p
                                role="alert"
                                className="text-destructive mt-(--spacing-1) text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height)"
                              >
                                {errorMessages[row.key]}
                              </p>
                            )}
                            {row.key === "zip" && <ZipLookupMessage status={zipLookupStatus} />}
                            {row.key === "workLocationZip" && <ZipLookupMessage status={workLocationZipLookupStatus} />}
                          </div>
                          <div className="max-w-[90%] self-center">
                            {hasSavedCounterpart ? (
                              <CallScriptFieldControl
                                row={row}
                                id={`${id}-saved`}
                                value={savedFields[row.key]}
                                onChange={() => {}}
                                disabled
                                ariaLabel={t("review.income.callScriptModal.savedFieldLabel", { field: row.label })}
                              />
                            ) : (
                              <span className="text-muted-foreground">{noSavedValue}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>

                {/* Side panel: Approval / Driver / Notes / History */}
                <div className="gap-(--spacing-2) flex min-h-0 flex-col">
                  <div className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) leading-tight">
                    <p>
                      <span className="text-primary font-(--font-weight-semibold)">
                        {t("review.income.callScriptModal.approvalNumber")}:
                      </span>{" "}
                      {approvalId}
                    </p>
                    <p>
                      <span className="text-primary font-(--font-weight-semibold)">
                        {t("review.income.callScriptModal.driver")}:
                      </span>{" "}
                      {driverName || noSavedValue}
                      {driverSsn && ` (${formatSsn(driverSsn)})`}
                    </p>
                  </div>

                  <div>
                    <label
                      htmlFor="call-script-note"
                      className="text-primary mb-(--spacing-1) text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold) block"
                    >
                      {t("review.income.callScriptModal.notes.label")}
                    </label>
                    <Textarea
                      id="call-script-note"
                      value={note}
                      onChange={(event) => setNote(event.target.value)}
                      placeholder={t("review.income.callScriptModal.notes.placeholder")}
                      disabled={readOnly || saveCallScript.isPending}
                      className="min-h-16 resize-none"
                    />
                  </div>

                  <CallScriptHistoryPanel
                    entries={entries}
                    isLoading={history.isLoading}
                    title={t("review.income.callScriptModal.history.title")}
                    loadingLabel={t("review.income.callScriptModal.history.loading")}
                    emptyLabel={t("review.income.callScriptModal.history.empty")}
                    entryHeader={historyEntryHeader}
                    renderAnswerValue={formatHistoryAnswerValue}
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="bg-muted px-(--spacing-4) py-(--spacing-2) border-t sm:flex-row">
              <Button type="button" size="xs" disabled={!canSave} onClick={handleSaveClick}>
                {t("review.income.callScriptModal.save")}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                disabled={!canSave}
                onClick={handleFaxVerificationClick}
              >
                {t("review.income.callScriptModal.faxVerification.open")}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                disabled={saveCallScript.isPending}
                onClick={() => onOpenChange(false)}
              >
                {t("review.income.callScriptModal.exit")}
              </Button>
            </DialogFooter>
          </div>
        )}
      </CallScriptDialogShell>

      <Dialog open={faxOpen} onOpenChange={setFaxOpen}>
        <DialogContent className="sm:max-w-md">
          <div className="space-y-(--spacing-4)">
            <div>
              <DialogTitle className="text-(length:--typography-base-sizes-large-font-size)/(--typography-base-sizes-large-line-height) font-(--font-weight-semibold)">
                {t("review.income.callScriptModal.faxVerification.title")}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height)">
                {t("review.income.callScriptModal.faxVerification.description")}
              </DialogDescription>
            </div>
            <div className="space-y-(--spacing-3)">
              <div className="space-y-(--spacing-1)">
                <label
                  htmlFor={`${uid}-fax-to`}
                  className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-medium)"
                >
                  {t("review.income.callScriptModal.faxVerification.faxTo")}
                </label>
                <Input
                  id={`${uid}-fax-to`}
                  value={fax.faxTo}
                  onChange={(event) => setFax((previous) => ({ ...previous, faxTo: event.target.value }))}
                />
              </div>
              <div className="space-y-(--spacing-1)">
                <label
                  htmlFor={`${uid}-fax-company-name`}
                  className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-medium)"
                >
                  {t("review.income.callScriptModal.faxVerification.faxCompanyName")}
                </label>
                <Input
                  id={`${uid}-fax-company-name`}
                  value={fax.faxCompanyName}
                  onChange={(event) => setFax((previous) => ({ ...previous, faxCompanyName: event.target.value }))}
                />
              </div>
              <div className="space-y-(--spacing-1)">
                <label
                  htmlFor={`${uid}-fax-number`}
                  className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-medium)"
                >
                  {t("review.income.callScriptModal.faxVerification.faxNumber")}
                </label>
                <Input
                  id={`${uid}-fax-number`}
                  value={fax.faxNumber}
                  onChange={(event) => setFax((previous) => ({ ...previous, faxNumber: event.target.value }))}
                />
              </div>
              <div className="space-y-(--spacing-1)">
                <label
                  htmlFor={`${uid}-fax-notes`}
                  className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-medium)"
                >
                  {t("review.income.callScriptModal.faxVerification.faxAddtlNotes")}
                </label>
                <Textarea
                  id={`${uid}-fax-notes`}
                  value={fax.faxAddtlNotes}
                  onChange={(event) => setFax((previous) => ({ ...previous, faxAddtlNotes: event.target.value }))}
                />
              </div>
            </div>
            <p className="text-muted-foreground text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height)">
              {t("review.income.callScriptModal.faxVerification.pendingBackendSupport")}
            </p>
            <div className="gap-(--spacing-2) flex justify-end">
              <Button type="button" variant="outline" size="xs" onClick={() => setFaxOpen(false)}>
                {t("review.income.callScriptModal.exit")}
              </Button>
              <Button
                type="button"
                size="xs"
                disabled
                title={t("review.income.callScriptModal.faxVerification.pendingBackendSupport")}
              >
                {t("review.income.callScriptModal.faxVerification.send")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <CallScriptConfirmSaveDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        rows={changedFieldRows}
        onConfirm={() => void handleConfirmSave()}
        disabled={isCheckingForceExit}
        error={confirmError}
        labels={{
          title: t("review.income.callScriptModal.confirmSave.title"),
          message: t("review.income.callScriptModal.confirmSave.message"),
          question: t("review.income.callScriptModal.confirmSave.question"),
          verifiedInformation: t("review.income.callScriptModal.verifiedInformation"),
          savedInformation: t("review.income.callScriptModal.savedInformation"),
          confirm: t("review.income.callScriptModal.confirmSave.ok"),
          cancel: t("review.income.callScriptModal.confirmSave.cancel"),
        }}
      />

      <ForceExitWarningModal
        open={forceExitWarningOpen}
        onOpenChange={setForceExitWarningOpen}
        onConfirm={() => setForceExitWarningOpen(false)}
      />
    </>
  );
}
